import { defineStore } from "pinia";
import { ref, computed, markRaw } from "vue";
import { toast as sonnerToast } from "vue-sonner";
import Toast from "@/components/episto-ui/toast/Toast.vue";

const RawToast = markRaw(Toast);

const DEFAULT_DURATION = {
  success: 5000,
  info: 5000,
  warning: 7000,
  error: Infinity,
  loading: Infinity,
};

export const useToastsStore = defineStore("toasts", () => {
  // Live per-sonner-id state. Plain Map: nothing in templates reads it,
  // making it reactive would just burn proxy overhead.
  const toastState = new Map();

  // Sonner caches each toast's height on mount and never re-measures, so a
  // same-id update with bigger content (loading → error) overlaps neighbours.
  // We remount under a fresh sonnerId on status changes; idMap keeps the
  // caller-facing id stable across remounts.
  const idMap = new Map();

  // Reactive: drives DismissAllButton visibility.
  const activeIds = ref(new Set());
  const count = computed(() => activeIds.value.size);

  let counter = 0;
  function generateId() {
    counter += 1;
    return `toast-${Date.now()}-${counter}`;
  }

  function registerActive(id) {
    if (id == null) return;
    activeIds.value = new Set(activeIds.value).add(id);
  }

  function unregisterActive(id) {
    if (id == null || !activeIds.value.has(id)) return;
    const next = new Set(activeIds.value);
    next.delete(id);
    activeIds.value = next;
  }

  function pushToSonner(id, state) {
    const isPersistent = !Number.isFinite(state.duration);
    const onClose = () => {
      const current = toastState.get(id);
      if (current && typeof current.onCancel === "function" && current.status === "loading") {
        current.onCancel();
      }
      sonnerToast.dismiss(id);
    };
    const componentProps = state.component
      ? { ...state.componentProps, status: state.status, onClose }
      : {
          status: state.status,
          title: state.title,
          message: state.message,
          progress: state.progress,
          duration: isPersistent ? 0 : state.duration,
          actions: state.actions,
          onClose,
          onAction: (action) => {
            if (typeof action?.onClick === "function") action.onClick();
          },
        };
    sonnerToast.custom(state.component ?? RawToast, {
      id,
      duration: isPersistent ? Infinity : state.duration,
      closeButton: false,
      class: "h-auto!",
      componentProps,
      onDismiss: () => {
        const current = toastState.get(id);
        toastState.delete(id);
        if (current?.callerId && idMap.get(current.callerId) === id) {
          idMap.delete(current.callerId);
        }
        unregisterActive(id);
        current?.onDismiss?.();
      },
      onAutoClose: () => {
        const current = toastState.get(id);
        toastState.delete(id);
        if (current?.callerId && idMap.get(current.callerId) === id) {
          idMap.delete(current.callerId);
        }
        unregisterActive(id);
        current?.onAutoClose?.();
      },
    });
  }

  function resolveDuration(status, options, existing) {
    if (options.duration !== undefined) return options.duration;
    // Loading toasts must stay until manually closed, even after transitioning.
    if (existing?.startedAsLoading) return Infinity;
    return DEFAULT_DURATION[status] ?? Infinity;
  }

  function rotateSonnerId(callerId, previousSonnerId, existing) {
    const originalCallbacks = {
      onCancel: existing.onCancel,
      onDismiss: existing.onDismiss,
      onAutoClose: existing.onAutoClose,
    };
    // Suppress user callbacks: a remount is not a close. The wrapper reads
    // these from toastState at fire time, so clearing them here is enough.
    existing.onDismiss = undefined;
    existing.onAutoClose = undefined;
    sonnerToast.dismiss(previousSonnerId);
    counter += 1;
    return {
      sonnerId: `${callerId}__${counter}`,
      originalCallbacks,
    };
  }

  function show(status, title, message = "", options = {}) {
    const callerId = options.id ?? generateId();
    const previousSonnerId = idMap.get(callerId);
    const existing = previousSonnerId ? toastState.get(previousSonnerId) : null;
    const statusChanged = existing && existing.status !== status;

    let sonnerId;
    let originalCallbacks;
    if (statusChanged) {
      ({ sonnerId, originalCallbacks } = rotateSonnerId(callerId, previousSonnerId, existing));
    } else {
      sonnerId = previousSonnerId ?? callerId;
      originalCallbacks = {
        onCancel: existing?.onCancel,
        onDismiss: existing?.onDismiss,
        onAutoClose: existing?.onAutoClose,
      };
    }
    idMap.set(callerId, sonnerId);

    // Progress bar stays at 100% on success but is dropped on error (a full
    // red bar reads as "completed" and contradicts the failure status).
    const hadProgress = existing?.progress !== undefined && existing?.progress !== null;
    let progress;
    if (options.progress !== undefined) progress = options.progress;
    else if (hadProgress && status !== "error") progress = 100;
    else progress = null;

    const state = {
      status,
      title,
      message,
      progress,
      actions: options.actions ?? existing?.actions ?? null,
      component: options.component ?? existing?.component ?? null,
      componentProps: options.componentProps ?? existing?.componentProps ?? null,
      duration: resolveDuration(status, options, existing),
      startedAsLoading: existing?.startedAsLoading ?? status === "loading",
      onCancel: options.onCancel ?? originalCallbacks.onCancel,
      onDismiss: options.onDismiss ?? originalCallbacks.onDismiss,
      onAutoClose: options.onAutoClose ?? originalCallbacks.onAutoClose,
      callerId,
    };

    toastState.set(sonnerId, state);
    registerActive(sonnerId);
    pushToSonner(sonnerId, state);
    return callerId;
  }

  function update(callerId, patch = {}) {
    if (!callerId) return;
    const previousSonnerId = idMap.get(callerId) ?? callerId;
    const existing = toastState.get(previousSonnerId);
    if (!existing) return;

    const statusChanged = patch.status !== undefined && patch.status !== existing.status;
    const status = patch.status ?? existing.status;

    let sonnerId;
    let originalCallbacks;
    if (statusChanged) {
      ({ sonnerId, originalCallbacks } = rotateSonnerId(callerId, previousSonnerId, existing));
      idMap.set(callerId, sonnerId);
    } else {
      sonnerId = previousSonnerId;
      originalCallbacks = {
        onCancel: existing.onCancel,
        onDismiss: existing.onDismiss,
        onAutoClose: existing.onAutoClose,
      };
    }

    let duration = existing.duration;
    if (patch.duration !== undefined) {
      duration = patch.duration;
    } else if (statusChanged) {
      duration = existing.startedAsLoading
        ? Infinity
        : DEFAULT_DURATION[status] ?? existing.duration;
    }

    const hadProgress = existing.progress !== undefined && existing.progress !== null;
    const progress =
      patch.progress !== undefined
        ? patch.progress
        : statusChanged && hadProgress && status !== "error"
        ? 100
        : statusChanged && status === "error"
        ? null
        : existing.progress;

    const state = {
      status,
      title: patch.title !== undefined ? patch.title : existing.title,
      message: patch.message !== undefined ? patch.message : existing.message,
      progress,
      actions: patch.actions !== undefined ? patch.actions : existing.actions,
      component: existing.component,
      componentProps: patch.componentProps !== undefined ? patch.componentProps : existing.componentProps,
      duration,
      startedAsLoading: existing.startedAsLoading,
      onCancel: patch.onCancel !== undefined ? patch.onCancel : originalCallbacks.onCancel,
      onDismiss: patch.onDismiss !== undefined ? patch.onDismiss : originalCallbacks.onDismiss,
      onAutoClose:
        patch.onAutoClose !== undefined ? patch.onAutoClose : originalCallbacks.onAutoClose,
      callerId,
    };

    if (statusChanged) registerActive(sonnerId);
    toastState.set(sonnerId, state);
    pushToSonner(sonnerId, state);
  }

  function dismiss(callerId) {
    if (!callerId) return;
    const sonnerId = idMap.get(callerId) ?? callerId;
    sonnerToast.dismiss(sonnerId);
  }

  function dismissAll() {
    [...activeIds.value].forEach((id) => sonnerToast.dismiss(id));
  }

  return {
    count,
    success: (title, message, options) => show("success", title, message, options),
    error: (title, message, options) => show("error", title, message, options),
    warning: (title, message, options) => show("warning", title, message, options),
    info: (title, message, options) => show("info", title, message, options),
    loading: (title, message, options) => show("loading", title, message, options),
    update,
    dismiss,
    dismissAll,
  };
});
